import moment from 'moment'
import VisibilityIcon from '@mui/icons-material/Visibility'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import PublicIcon from '@mui/icons-material/Public'
import AddLinkIcon from '@mui/icons-material/AddLink'
import LinkOffIcon from '@mui/icons-material/LinkOff'
import EditNoteIcon from '@mui/icons-material/EditNote'
import HistoryIcon from '@mui/icons-material/History'

// How each access-log `kind` (classified on the server by
// helpers/accessLogKind.js) is presented. Both the per-record drawer and the
// dashboard board read from here so an activity always looks the same.
export const ACCESS_LOG_KIND_META = {
  viewed: { label: 'Record viewed', color: 'anMainBlue', Icon: VisibilityIcon },
  downloaded: { label: 'PDF downloaded', color: 'anPurple', Icon: PictureAsPdfIcon },
  external: { label: 'Share code download', color: 'warning', Icon: PublicIcon },
  shareCreated: { label: 'Share code created', color: 'success', Icon: AddLinkIcon },
  shareRevoked: { label: 'Share code revoked', color: 'error', Icon: LinkOffIcon },
  updated: { label: 'Record updated', color: 'info', Icon: EditNoteIcon },
  other: { label: 'Activity', color: 'default', Icon: HistoryIcon },
}

export const FILTERABLE_KINDS = [
  'viewed',
  'downloaded',
  'external',
  'shareCreated',
  'shareRevoked',
  'updated',
]

export const normaliseKind = (kind) =>
  ACCESS_LOG_KIND_META[kind] ? kind : 'other'

export const kindMeta = (kind) => ACCESS_LOG_KIND_META[normaliseKind(kind)]

export const accessLogActor = (log) => {
  if (log?.accessedBy) {
    const name = [log.accessedBy.firstName, log.accessedBy.lastName]
      .filter(Boolean)
      .join(' ')
    return {
      name: name || 'Staff member',
      photograph: log.accessedBy.photograph,
      external: false,
    }
  }
  if (log?.accessedByCustom) {
    return { name: log.accessedByCustom, photograph: null, external: true }
  }
  return { name: 'System', photograph: null, external: false, system: true }
}

// One line saying what happened, e.g. "Viewed · Audit".
export const accessLogSummary = (log) => {
  switch (log?.kind) {
    case 'viewed':
      return log.detail ? `Viewed · ${log.detail}` : 'Viewed the record'
    case 'downloaded':
      return 'Downloaded the PDF'
    case 'external':
      return `Downloaded via share code${log.code ? ` ${log.code}` : ''}`
    case 'shareCreated':
      return `Created share code${log.code ? ` ${log.code}` : ''}`
    case 'shareRevoked':
      return `Revoked share code${log.code ? ` ${log.code}` : ''}`
    case 'updated':
      return log.detail ? `Updated the record · ${log.detail}` : 'Updated the record'
    default:
      return log?.detail || log?.note || kindMeta(log?.kind).label
  }
}

const patientLabel = (demographics) => {
  if (!demographics) return ''
  const lastName = demographics.lastNameUnknown
    ? 'UNKNOWN'
    : demographics.lastName
  const initial = demographics.firstName ? demographics.firstName[0] : ''
  return [lastName, initial].filter(Boolean).join(', ')
}

// Which record a board row belongs to, and where to open it.
export const accessLogRecord = (log) => {
  if (log?.epcr) {
    return {
      number: log.epcr.epcrNumber,
      patient: patientLabel(log.epcr.demographics),
      date: log.epcr.incident?.incidentDate,
      type: ['Welfare', 'Critical Care'].includes(log.epcr.mode)
        ? log.epcr.mode
        : 'ePCR',
      href: `/patient-report-forms/${log.epcr._id}`,
    }
  }
  if (log?.paperPCR) {
    return {
      number: log.paperPCR.pcrNumber,
      patient: patientLabel(log.paperPCR.demographics),
      date: log.paperPCR.incidentDate,
      type: 'Paper',
      href: `/patient-report-forms/paper/${log.paperPCR._id}`,
    }
  }
  if (log?.uninjuredCompetitorReport) {
    return {
      number: log.uninjuredCompetitorReport.competitorNumber,
      patient: '',
      date: null,
      type: 'Uninjured',
      href: `/patient-report-forms/ucr/${log.uninjuredCompetitorReport._id}`,
    }
  }
  return { number: null, patient: 'Record no longer available', type: null, href: null }
}

export const dayHeading = (date) => {
  const value = moment(date)
  if (value.isSame(moment(), 'day')) return 'Today'
  if (value.isSame(moment().subtract(1, 'day'), 'day')) return 'Yesterday'
  return value.format('dddd D MMMM YYYY')
}
